import './index.css';

import { WorkspaceDBService } from '@affine/core/modules/db';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import { DocsStore } from '@affine/core/modules/doc/stores/docs';
import { AccessibleFolderCacheStore } from '@affine/core/modules/organize';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import {
  createContext,
  isValidElement,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { combineLatest, map } from 'rxjs';

type Entity = { type: 'doc' | 'folder'; id: string };
type SearchDoc = {
  id: string;
  title: string;
  trashed: boolean;
  parentTrashed?: boolean;
  parentId?: string | null;
};
type SearchFolder = {
  id: string;
  name: string;
  parentId: string | null;
  docIds: string[];
};
type Branch = {
  id: string;
  parent: string | null;
  label: string;
  entity?: Entity;
};
type Search = {
  active: boolean;
  animate: boolean;
  visible: ReadonlySet<string>;
  docs: ReadonlySet<string>;
  folders: ReadonlySet<string>;
  register: (branch: Branch) => () => void;
};

const SearchContext = createContext<Search | null>(null);
const ParentContext = createContext<string | null>(null);

function normalize(value: string) {
  return value.normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase();
}

function labelText(label: ReactNode): string {
  if (typeof label === 'string' || typeof label === 'number')
    return String(label);
  if (Array.isArray(label)) return label.map(labelText).join(' ');
  if (isValidElement<{ children?: ReactNode; rawName?: string }>(label)) {
    return label.props.rawName ?? labelText(label.props.children);
  }
  return '';
}

/** Index accessible metadata; renderers continue enforcing their own permissions. */
export function SidebarTreeSearchProvider({
  query,
  children,
}: {
  query: string;
  children: ReactNode;
}) {
  const docCache = useService(AccessibleDocCacheStore);
  const docsStore = useService(DocsStore);
  const dbService = useService(WorkspaceDBService);
  const folderCache = useService(AccessibleFolderCacheStore);
  const workspace = useService(WorkspaceService).workspace;
  const local = workspace.flavour === 'local';
  const docs = useLiveData(
    useMemo(
      () =>
        LiveData.from<SearchDoc[]>(
          local
            ? combineLatest([
                docsStore.watchAllDocTitle(),
                docsStore.watchNonTrashDocIds(),
              ]).pipe(
                map(([titles, ids]) => {
                  const visible = new Set(ids);
                  return titles
                    .filter(doc => visible.has(doc.id))
                    .map(doc => ({
                      ...doc,
                      trashed: false,
                      parentTrashed: false,
                      parentId: null,
                    }));
                })
              )
            : docCache.watchAllAccessibleDocs(),
          local ? [] : docCache.getCachedDocs()
        ),
      [docCache, docsStore, local]
    )
  );
  const folders = useLiveData(
    useMemo(
      () =>
        LiveData.from<SearchFolder[]>(
          local
            ? dbService.db.folders.find$().pipe(
                map(rows => {
                  const links = new Map<string, string[]>();
                  rows.forEach(row => {
                    if (row.type !== 'doc' || row.trashed || !row.parentId)
                      return;
                    const ids = links.get(row.parentId) ?? [];
                    ids.push(row.data);
                    links.set(row.parentId, ids);
                  });
                  return rows
                    .filter(row => row.type === 'folder' && !row.trashed)
                    .map(row => ({
                      id: row.id,
                      name: row.data,
                      parentId: row.parentId ?? null,
                      docIds: links.get(row.id) ?? [],
                    }));
                })
              )
            : folderCache.watchAllAccessibleFolders(),
          local ? [] : folderCache.getCachedFolders()
        ),
      [dbService, folderCache, local]
    )
  );
  const [branches, setBranches] = useState(new Map<string, Branch>());
  const register = useCallback((branch: Branch) => {
    setBranches(previous => new Map(previous).set(branch.id, branch));
    return () =>
      setBranches(previous => {
        if (previous.get(branch.id) !== branch) return previous;
        const next = new Map(previous);
        next.delete(branch.id);
        return next;
      });
  }, []);
  const tokens = useMemo(
    () => normalize(query).trim().split(/\s+/).filter(Boolean),
    [query]
  );
  const active = tokens.length > 0;
  // Restore persisted geometry in one commit before enabling user motions again.
  const [committedActive, setCommittedActive] = useState(active);
  useLayoutEffect(() => {
    setCommittedActive(active);
  }, [active]);
  const animate = !active && !committedActive;
  const matches = useCallback(
    (label: string) => {
      const text = normalize(label);
      return tokens.every(token => text.includes(token));
    },
    [tokens]
  );

  const indexed = useMemo(() => {
    const matchedDocs = new Set<string>();
    const matchedFolders = new Set<string>();
    if (!active) return { docs: matchedDocs, folders: matchedFolders };
    docs.forEach(doc => {
      if (!doc.trashed && !doc.parentTrashed && matches(doc.title))
        matchedDocs.add(doc.id);
    });
    const byId = new Map(folders.map(folder => [folder.id, folder]));
    const retainPath = (id: string) => {
      let current: string | null = id;
      while (current && !matchedFolders.has(current)) {
        matchedFolders.add(current);
        current = byId.get(current)?.parentId ?? null;
      }
    };
    docs.forEach(doc => {
      if (matchedDocs.has(doc.id) && doc.parentId && byId.has(doc.parentId)) {
        retainPath(doc.parentId);
      }
    });
    folders.forEach(folder => {
      if (
        matches(folder.name) ||
        folder.docIds.some(id => matchedDocs.has(id))
      ) {
        retainPath(folder.id);
      }
    });
    return { docs: matchedDocs, folders: matchedFolders };
  }, [active, docs, folders, matches]);

  const visible = useMemo(() => {
    const result = new Set<string>();
    if (!active) return result;
    branches.forEach(branch => {
      const entityMatch =
        branch.entity?.type === 'folder'
          ? indexed.folders.has(branch.entity.id)
          : branch.entity?.type === 'doc' && indexed.docs.has(branch.entity.id);
      if (!entityMatch && !matches(branch.label)) return;
      let current: string | null = branch.id;
      while (current && !result.has(current)) {
        result.add(current);
        current = branches.get(current)?.parent ?? null;
      }
    });
    return result;
  }, [active, branches, indexed, matches]);

  const value = useMemo(
    () => ({
      active,
      animate,
      visible,
      docs: indexed.docs,
      folders: indexed.folders,
      register,
    }),
    [active, animate, visible, indexed.docs, indexed.folders, register]
  );
  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

/** Register only nodes that their existing renderer actually permits. */
export function useSidebarSearchBranch(label: ReactNode, entity?: Entity) {
  const search = useContext(SearchContext);
  const parent = useContext(ParentContext);
  const id = useId();
  const text = labelText(label);
  const register = search?.register;
  const type = entity?.type;
  const entityId = entity?.id;
  useLayoutEffect(
    () =>
      register?.({
        id,
        parent,
        label: text,
        entity: type && entityId ? { type, id: entityId } : undefined,
      }),
    [id, parent, text, register, type, entityId]
  );
  return {
    id,
    active: search?.active ?? false,
    animate: search?.animate ?? true,
    visible: !search?.active || search.visible.has(id),
  };
}

export function SidebarSearchBranch({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  return <ParentContext.Provider value={id}>{children}</ParentContext.Provider>;
}

/** Open matching folder paths without writing the user's persisted collapse state. */
export function useSidebarSearchCollapsed(collapsed: boolean, path: string[]) {
  const search = useContext(SearchContext);
  if (!search?.active) return collapsed;
  const folderId =
    path
      .filter(part => part.startsWith('folder-'))
      .at(-1)
      ?.slice(7) ?? (path[0] === 'teamspace' ? path[1] : undefined);
  // Category sections discover their direct rows; folders only load matching paths.
  return folderId ? !search.folders.has(folderId) : false;
}

const NO_DOCS: ReadonlySet<string> = new Set();

/** The documents whose title answers the search, for a list that mounts only part of its rows. */
export function useSidebarSearchDocs() {
  const search = useContext(SearchContext);
  return { active: search?.active ?? false, docs: search?.docs ?? NO_DOCS };
}

export function useSidebarSearchState() {
  const search = useContext(SearchContext);
  return {
    active: search?.active ?? false,
    animate: search?.animate ?? true,
    hasMatches: !!search?.visible.size,
  };
}
